import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Panel from "./Panel";
import { getOpenWorkSeverity, getOpenWorkSeverityLabel } from "./openWorkSeverity";

const TONE_STYLES = {
  clear: "border-default bg-surface text-secondary",
  few: "border-warning bg-warning-subtle text-warning",
  many: "border-danger bg-danger-subtle text-danger",
};

function toneClass(count) {
  return TONE_STYLES[getOpenWorkSeverity(count)];
}

function ActionLink({ href, onClick, label = "Open" }) {
  if (href) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
      >
        {label}
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
      >
        {label}
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    );
  }
  return null;
}

export default function OpenWorkPanel({
  title = "Open Work",
  subtitle = "Pending or incomplete operational items that still need action.",
  items = [],
  emptyTitle = "No open work right now",
  emptyDescription = "This area is clear for the current scope.",
}) {
  return (
    <Panel title={title} subtitle={subtitle}>
      {!items.length ? (
        <div className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3.5 py-3 text-sm text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800/35 dark:text-neutral-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500" />
          <div>
            <div className="font-medium text-neutral-900 dark:text-neutral-100">{emptyTitle}</div>
            <div className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{emptyDescription}</div>
          </div>
        </div>
      ) : (
        <div className="grid gap-2.5 lg:grid-cols-2 2xl:grid-cols-3">
          {items.map((item) => (
            <div
              key={item.id || item.title}
              className={`rounded-xl border px-3.5 py-3 ${toneClass(item.count)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{item.title}</div>
                  {item.description ? (
                    <div className="mt-1 text-xs opacity-80">{item.description}</div>
                  ) : null}
                </div>
                {item.count !== undefined && item.count !== null ? (
                  <span
                    aria-label={`${item.count}, ${getOpenWorkSeverityLabel(item.count).toLocaleLowerCase()}`}
                    className="inline-flex shrink-0 rounded-full border border-current/20 bg-white/60 px-2 py-0.5 text-xs font-semibold dark:bg-black/10"
                  >
                    {item.count}
                  </span>
                ) : null}
              </div>

              {Array.isArray(item.tags) && item.tags.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex rounded-full border border-current/15 bg-white/50 px-2 py-0.5 text-[11px] font-medium dark:bg-black/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}

              {item.meta ? (
                <div className="mt-3 text-xs opacity-80">{item.meta}</div>
              ) : null}

              {(item.href || item.onClick) ? (
                <div className="mt-3">
                  <ActionLink href={item.href} onClick={item.onClick} label={item.actionLabel || "Open"} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
